import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Area, AreaChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';

import { getMcpUsage, type McpUsageCounts, type McpUsageSummaryResponse } from '../api/client';

const metrics = ['total_calls', 'successful_calls', 'failed_calls'] as const;

function CountsTable({ heading, rows }: {
  heading: string;
  rows: (McpUsageCounts & { id: string; label: string })[];
}) {
  const { t, i18n } = useTranslation();
  return (
    <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white">
      <table className="min-w-full divide-y divide-gray-200 text-sm">
        <caption className="p-4 text-start font-semibold text-gray-900">{heading}</caption>
        <thead className="bg-gray-50 text-gray-600"><tr>
          <th scope="col" className="px-4 py-3 text-start">{t('settings.usage.mcp.dimension')}</th>
          {metrics.map((metric) => (
            <th key={metric} scope="col" className="px-4 py-3 text-end">{t(`settings.usage.mcp.${metric}`)}</th>
          ))}
        </tr></thead>
        <tbody className="divide-y divide-gray-100">
          {rows.map((row) => <tr key={row.id}>
            <th scope="row" className="max-w-xs break-words px-4 py-3 text-start font-normal"><bdi>{row.label}</bdi></th>
            {metrics.map((metric) => (
              <td key={metric} className="px-4 py-3 text-end">{row[metric].toLocaleString(i18n.language)}</td>
            ))}
          </tr>)}
        </tbody>
      </table>
    </div>
  );
}

export default function McpUsageAnalytics({ tenantId, from, to, refresh }: {
  tenantId: string; from: string; to: string; refresh: number;
}) {
  const { t, i18n } = useTranslation();
  const [data, setData] = useState<McpUsageSummaryResponse | null>(null);
  const [error, setError] = useState(false);
  const [retry, setRetry] = useState(0);

  useEffect(() => {
    const controller = new AbortController();
    setData(null);
    setError(false);
    getMcpUsage(from, to, controller.signal)
      .then((result) => {
        if (controller.signal.aborted) return;
        if (result.tenant_id !== tenantId || result.from_date !== from || result.to_date !== to) {
          throw new Error('MCP usage response does not match the requested scope');
        }
        setData(result);
      })
      .catch(() => { if (!controller.signal.aborted) setError(true); });
    return () => controller.abort();
  }, [tenantId, from, to, refresh, retry]);

  return (
    <section className="min-w-0 space-y-4 border-t border-gray-200 pt-6" aria-label={t('settings.usage.mcp.title')} data-testid="mcp-usage-analytics">
      <div>
        <h2 className="font-semibold text-gray-900">{t('settings.usage.mcp.title')}</h2>
        <p className="mt-1 text-sm text-gray-500">{t('settings.usage.mcp.guidance')}</p>
      </div>
      {!data && !error && <p role="status" className="p-4 text-gray-500">{t('common.loading')}</p>}
      {error && <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
        <p>{t('settings.usage.mcp.loadError')}</p>
        <button type="button" className="mt-2 rounded border border-red-300 px-3 py-2" onClick={() => setRetry((value) => value + 1)}>
          {t('common.retry')}
        </button>
      </div>}
      {data && <>
        <p className="text-xs text-gray-500">{t('settings.usage.timezoneNote', { timezone: data.timezone })}</p>
        <div className="grid gap-3 sm:grid-cols-3">
          {metrics.map((metric) => <div key={metric} className="rounded-lg border border-gray-200 bg-white p-4">
            <p className="text-sm text-gray-500">{t(`settings.usage.mcp.${metric}`)}</p>
            <p className="mt-1 text-2xl font-semibold text-gray-900" data-testid={`mcp-usage-${metric}`}>{data.totals[metric].toLocaleString(i18n.language)}</p>
          </div>)}
        </div>
        {!data.totals.total_calls ? (
          <p className="rounded-lg border border-gray-200 bg-white p-6 text-gray-500">{t('settings.usage.mcp.empty')}</p>
        ) : <>
          <div className="rounded-lg border border-gray-200 bg-white p-4">
            <h3 className="mb-4 font-semibold text-gray-900">{t('settings.usage.mcp.daily')}</h3>
            <div className="h-64" aria-hidden="true" dir="ltr">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={data.daily}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                  <YAxis allowDecimals={false} />
                  <Tooltip />
                  <Legend verticalAlign="top" height={48} wrapperStyle={{ fontSize: 12 }} />
                  <Area type="monotone" dataKey="successful_calls" name={t('settings.usage.mcp.successful_calls')} stroke="#059669" fill="#a7f3d0" />
                  <Area type="monotone" dataKey="failed_calls" name={t('settings.usage.mcp.failed_calls')} stroke="#dc2626" fill="#fecaca" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
            <details className="mt-3">
              <summary className="cursor-pointer text-sm text-primary-700">{t('settings.usage.mcp.dailyTable')}</summary>
              <CountsTable heading={t('settings.usage.mcp.daily')} rows={data.daily.map((row) => ({ ...row, id: row.date, label: row.date }))} />
            </details>
          </div>
          <CountsTable heading={t('settings.usage.mcp.byTool')} rows={data.tools.map((row) => ({
            ...row, id: row.tool_name,
            label: row.tool_name === '__unknown_tool__' ? t('settings.usage.mcp.unknownTool') : row.tool_name,
          }))} />
          <CountsTable heading={t('settings.usage.mcp.byUser')} rows={data.principals.map((row) => ({
            ...row, id: row.principal_id,
            label: row.user_id ? (row.name || t('settings.usage.mcp.formerMember')) : t('settings.usage.mcp.shared'),
          }))} />
        </>}
      </>}
    </section>
  );
}
